/**
 * Agent flight HR margin: greater of package % or a USD-per-passenger floor.
 * Apply only on the flight HR branch — hotels / packages / organized tours stay percent-only.
 *
 * Packaged RT/OW: $30 per passenger per booking (one itinerary).
 * Self-compose (הרכבה ידנית): $30 or the % — whichever is greater — **per direction**.
 * RT self-compose = two directions → $60/pax floor, not $30 total.
 */

export const FLIGHT_HR_COMMISSION_MIN_USD_PER_PAX = 30;

/**
 * How many OW directions the $30 / % floor applies to.
 * Default 1 (packaged itinerary). Self-compose combined deals are 2.
 */
export function resolveFlightHrDirectionCount(meta = {}) {
  const raw = Number(
    meta.flightHrDirectionCount ?? meta.directionCount ?? meta.selfComposeDirectionCount
  );
  if (Number.isFinite(raw) && raw >= 1) return Math.min(8, Math.floor(raw));
  if (meta.isSelfComposeCombined === true) return 2;
  const id = String(meta.dealId ?? meta.dealid ?? "");
  if (id.startsWith("SC-")) return 2;
  return 1;
}

/**
 * @param {object} [meta]
 * @param {boolean} [meta.isHotelDeal]
 * @param {string|null|undefined} [meta.ldsHrProductType]
 * @param {boolean} [meta.enforceFlightHrCommissionSsot]
 */
export function shouldApplyFlightHrCommissionMinFloor(meta = {}) {
  if (meta?.isHotelDeal === true) return false;
  const pt = String(meta?.ldsHrProductType ?? "")
    .trim()
    .toLowerCase();
  if (pt === "hotel" || pt === "vacation_package" || pt === "organized_tour") {
    return false;
  }
  return pt === "flight" || meta?.enforceFlightHrCommissionSsot === true;
}

/**
 * Minimum HR amount in the same currency as the fare base
 * ($30 × pax × directions × FX).
 * @param {object} [p]
 * @param {number} [p.passengersCount=1]
 * @param {number} [p.directionCount=1] — 2 for self-compose RT
 * @param {number} [p.usdToCurrencyRate=1] — 1 when the base is already USD
 * @param {number} [p.minUsdPerPax]
 */
export function flightHrCommissionMinAmount({
  passengersCount = 1,
  usdToCurrencyRate = 1,
  minUsdPerPax = FLIGHT_HR_COMMISSION_MIN_USD_PER_PAX,
  directionCount = 1,
} = {}) {
  const pax = Math.max(1, Math.floor(Number(passengersCount) || 1));
  const directions = Math.max(1, Math.floor(Number(directionCount) || 1));
  const rate = Number(usdToCurrencyRate);
  const minUsd = Number(minUsdPerPax);
  if (!(Number.isFinite(minUsd) && minUsd > 0)) return 0;
  if (!(Number.isFinite(rate) && rate > 0)) return 0;
  return minUsd * pax * directions * rate;
}

/**
 * Flight HR = max(percent-of-base, $30 × pax × directions).
 * `percentAmount` and the return value are in the same currency.
 *
 * @param {object} p
 * @param {number} p.percentAmount — HR already computed with existing % math
 * @param {number} [p.passengersCount=1]
 * @param {number} [p.directionCount=1]
 * @param {number} [p.minUsdPerPax]
 * @param {number} [p.usdToCurrencyRate=1]
 * @param {(n: number) => number} [p.roundFn]
 */
export function resolveFlightHrCommissionAmount({
  percentAmount,
  passengersCount = 1,
  directionCount = 1,
  minUsdPerPax = FLIGHT_HR_COMMISSION_MIN_USD_PER_PAX,
  usdToCurrencyRate = 1,
  roundFn,
} = {}) {
  const pct = Number(percentAmount);
  const safePct = Number.isFinite(pct) && pct > 0 ? pct : 0;
  const floorAmount = flightHrCommissionMinAmount({
    passengersCount,
    directionCount,
    usdToCurrencyRate,
    minUsdPerPax,
  });
  const raw = Math.max(safePct, floorAmount);
  if (typeof roundFn === "function") {
    const rounded = roundFn(raw);
    return Number.isFinite(Number(rounded)) ? Number(rounded) : raw;
  }
  return raw;
}
